import { DateTimeIcon } from "@components/icon";
import { TInfoValue } from "@components/smart/FieldInfo";
import { isDefined, isNotDefined, mouseDownRepeat } from "@utils/general";
import React from "react";

import { BasicInputSizes, IconSize, TextAlign } from "../../../enums";
import { KeyName } from "../../../keyName";
import DateType, { getDateFormat, getUtcDayjs } from "../../../types/Date";
import { WithErrorAndTooltipProps } from "../formError/WithErrorAndTooltip";
import { IInputProps, InputWithErrorAndTooltip } from "../input";
import { withFormatter } from "../input/WithFormatter";
import { Stepper } from "../numericValueInputBase/Stepper";
import { formatTimeVariants, longDateFormat, parseTimeUserInput } from "./utils";

const getFormat = (): string => {
    return longDateFormat(getDateFormat(DateType.defaultTimeFormat));
};


export const TimeInput = withFormatter<IInputProps & WithErrorAndTooltipProps, Date>({
    parser: (value: string, strict: boolean): Date => {
        return DateType.parse({
            date: parseTimeUserInput(value),
            format: formatTimeVariants(longDateFormat(getDateFormat(DateType.defaultTimeFormat))),
            strictMode: strict
        });
    },
    formatter: (value: Date): string => {
        return DateType.format(value, getFormat());
    },
    isValid: (value: Date): boolean => {
        if (Number(value) === 0) {
            return true;
        }
        return DateType.isValid(value);
    },
    isSame: (date1: Date, date2: Date): boolean => {
        if (date1 instanceof Date) {
            return date1.getMinutes() === date2?.getMinutes() && date1.getHours() === date2?.getHours();
        }
        return date1 === date2;
    }
}, InputWithErrorAndTooltip);

export interface ITimePickerSettings {
    minDate?: TInfoValue<Date>;
    maxDate?: TInfoValue<Date>;
    showSteppers?: boolean;
    withIcon?: boolean;
}

interface IProps extends IInputProps<Date>, WithErrorAndTooltipProps, Pick<ITimePickerSettings, "showSteppers" | "withIcon"> {
    minDate?: Date;
    maxDate?: Date;
}

export class TimePicker extends React.PureComponent<IProps> {
    handleNextMouseDown = (): void => {
        mouseDownRepeat(this.increment);
    };

    handlePrevMouseDown = (): void => {
        mouseDownRepeat(this.decrement);
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === KeyName.ArrowDown) {
            event.preventDefault();
            this.decrement();
        } else if (event.key === KeyName.ArrowUp) {
            event.preventDefault();
            this.increment();
        }

        this.props.onKeyDown?.(event);
    };


    increment = (): void => {
        this.updateValue(getUtcDayjs(this.props.value).add(1, "minute").toDate());
    };

    decrement = (): void => {
        this.updateValue(getUtcDayjs(this.props.value).subtract(1, "minute").toDate());
    };

    canIncrement = (): boolean => {
        if (!DateType.isValid(this.props.value)) {
            return false;
        }

        const date = getUtcDayjs(this.props.value);

        return isNotDefined(this.props.maxDate) || date.isBefore(this.props.maxDate);
    };

    canDecrement = (): boolean => {
        if (!DateType.isValid(this.props.value)) {
            return false;
        }

        const date = getUtcDayjs(this.props.value);

        return isNotDefined(this.props.maxDate) || date.isAfter(this.props.minDate);
    };

    updateValue = (value: Date): void => {
        let newVal = getUtcDayjs(value);

        if (isDefined(this.props.maxDate) && newVal.isAfter(this.props.maxDate)) {
            newVal = getUtcDayjs(this.props.maxDate);
        }

        if (isDefined(this.props.minDate) && newVal.isBefore(this.props.minDate)) {
            newVal = getUtcDayjs(this.props.minDate);
        }

        // always focus the input before changing the value, so validations and _wasChangedSinceBlur flag works correctly
        // this.inputRef.current?.focus();

        this.props.onChange?.({
            value: newVal.toDate(),
            triggerAdditionalTasks: true
        });
    };

    renderSteppers = (): React.ReactElement => {
        return (
            <Stepper
                isLight={this.props.isLight}
                isDisabled={this.props.isDisabled}
                onNextMouseDown={this.handleNextMouseDown}
                onPrevMouseDown={this.handlePrevMouseDown}
                isNextDisabled={!this.canIncrement()}
                isPrevDisabled={!this.canDecrement()}
            />
        );
    };

    render() {
        return (
            <TimeInput {...this.props}
                       value={this.props.value}
                       icon={this.props.icon ?? (this.props.withIcon ? (<DateTimeIcon width={IconSize.M} />) : null)}
                       textAlign={TextAlign.Right}
                       width={this.props.width ?? BasicInputSizes.S}
                       content={this.props.showSteppers ? this.renderSteppers() : null}
                       onKeyDown={this.handleKeyDown}
                       placeholder={getFormat()?.toLowerCase()}/>
        );
    }
}